// 权限状态管理模块 - 统一权限验证逻辑
import axios from '@/axios';

const state = {
  permissions: [],
  loading: false,
  initialized: false,
  error: null
};

const getters = {
  // 获取所有权限
  allPermissions: state => state.permissions,
  
  // 检查是否有指定路径权限
  hasPathPermission: state => path => {
    return state.permissions.some(perm => 
      path.startsWith(perm.path) || perm.path === '*'
    );
  },
  
  // 检查是否有指定操作权限
  hasActionPermission: state => action => {
    return state.permissions.some(perm => 
      perm.actions && perm.actions.includes(action)
    );
  },

  // 根据权限生成菜单
  getMenus: state => {
    // 后端现在直接返回菜单结构的列表，存储在 state.permissions 中
    // 直接返回这个列表即可
    return state.permissions;
  },
  
  // 权限是否已初始化
  isPermissionInitialized: state => state.initialized,
  
  // 权限是否正在加载
  isPermissionLoading: state => state.loading
};

const mutations = {
  SET_PERMISSIONS(state, permissions) {
    state.permissions = permissions;
    state.initialized = true;
  },
  SET_LOADING(state, loading) {
    state.loading = loading;
  },
  SET_ERROR(state, error) {
    state.error = error;
  },
  RESET_PERMISSIONS(state) {
    state.permissions = [];
    state.initialized = false;
    state.error = null;
  }
};

const actions = {
  // 获取用户权限
  async fetchPermissions({ commit, state }) {
    if (state.loading) return;
    
    try {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);
      
      // 调用后端接口获取权限数据
      const response = await axios.post('userauth/permissions/');
      
      if (response.data.status === 10000) {
        commit('SET_PERMISSIONS', response.data.data.permissions || []);
        return response.data.data.permissions;
      } else {
        throw new Error(response.data.message || '获取权限数据失败');
      }
    } catch (error) {
      commit('SET_ERROR', error.message || '获取权限数据失败');
      console.error('获取权限数据失败:', error);
      return [];
    } finally {
      commit('SET_LOADING', false);
    }
  },
  
  // 重置权限状态
  resetPermissions({ commit }) {
    commit('RESET_PERMISSIONS');
  },
  
  // 验证页面访问权限
  checkPagePermission({ state, dispatch }, path) {
    // 如果权限未初始化，先获取权限
    if (!state.initialized && !state.loading) {
      return dispatch('fetchPermissions').then(permissions => {
        return permissions.some(perm => path.startsWith(perm.path) || perm.path === '*');
      });
    }
    
    // 已有权限数据，直接验证
    return Promise.resolve(state.permissions.some(perm => 
      path.startsWith(perm.path) || perm.path === '*'
    ));
  }
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
};

// 导出辅助函数，方便在组件中使用
export const usePermission = (store) => {
  return {
    // 检查路径权限
    checkPath: (path) => store.getters['permission/hasPathPermission'](path),
    
    // 检查操作权限
    checkAction: (action) => store.getters['permission/hasActionPermission'](action),
    
    // 获取权限状态
    getState: () => ({
      loading: store.getters['permission/isPermissionLoading'],
      initialized: store.getters['permission/isPermissionInitialized'],
      error: store.state.permission.error
    }),
    
    // 获取所有权限
    getPermissions: () => store.getters['permission/allPermissions'],
    
    // 初始化权限
    init: () => store.dispatch('permission/fetchPermissions'),
    
    // 验证页面权限
    verifyPage: (path) => store.dispatch('permission/checkPagePermission', path)
  };
};